import { Button, Input } from "@repo/ui"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "react-router"
import { useTranslation } from "react-i18next"
import { Check, Spinner } from "@phosphor-icons/react"
import { useMemo } from "react"
import { useForgotPasswordMutation } from "@/feature/auth/api"
import { LogoExtended } from "@/routes/layouts/public-layout"

const validationSchema = z.object({
  email: z.string().email(),
})

type FormValues = z.infer<typeof validationSchema>

export const SendResetMail = () => {
  const { t } = useTranslation(`wizard`)
  const forgotPasswordMutation = useForgotPasswordMutation()

  const { register, formState, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      email: ``,
    },
  })

  const onSubmit = handleSubmit((data) => {
    forgotPasswordMutation.mutate(data.email)
  })

  const isLoginButtonDisabled =
    forgotPasswordMutation.isPending || !formState.isValid

  const buttonContent = useMemo(() => {
    if (forgotPasswordMutation.isPending) {
      return <Spinner className="animate-spin" />
    }
    if (forgotPasswordMutation.isSuccess) {
      return <Check size={20} />
    }
    return <span>{t(`login.screens.forgot_password.from.button`)}</span>
  }, [forgotPasswordMutation.isPending, forgotPasswordMutation.isSuccess, t])

  return (
    <div className="bg-white w-full p-8 h-full flex flex-col items-center justify-between">
      <LogoExtended />
      <div className="max-w-md w-full">
        <h1 className="text-4xl font-medium text-center">
          {t(`login.screens.forgot_password.title`)}
        </h1>
        <p className="text-secondary max-w-sm mx-auto text-lg text-center">
          {t(`login.screens.forgot_password.description`)}
        </p>
        <div id="kc-form">
          <div id="kc-form-wrapper">
            <form
              onSubmit={onSubmit}
              className="w-full flex flex-col gap-4 mt-10"
            >
              <div className="w-full">
                <Input
                  {...register(`email`)}
                  type="text"
                  autoComplete="email"
                />

                {forgotPasswordMutation.isSuccess ? (
                  <span className="text-green-300 text-sm mt-1">
                    {t(`login.screens.forgot_password.success.title`)}
                  </span>
                ) : null}
              </div>

              <Button
                name="login"
                type="submit"
                disabled={isLoginButtonDisabled}
              >
                {buttonContent}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 justify-center">
        <p className="text-muted-foreground">
          {t(`sign_up.screens.welcome.login.text`)}
        </p>
        <Button asChild variant="link" size="sm">
          <Link to="/auth/login">
            {t(`sign_up.screens.welcome.login.button`)}
          </Link>
        </Button>
      </div>
    </div>
  )
}
